<template>
  <div class="px-2 py-4 sm:px-4 sm:py-6">
    <!-- Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isModalOpen"
      sync-type="prospects"
      :progress="progress"
      :has-error="hasError"
      :error-message="errorMessage"
      @close="closeModal"
      @retry="retrySync"
    />

    <!-- Sync Results -->
    <SyncResultNotification :result="syncResult" @close="syncResult = null" />

    <!-- Filter tabs and search - sticky header -->
    <PageTabs v-model="activeTab" :tabs="tabs">
      <template #controls>
        <!-- Team filter dropdown -->
        <Listbox v-slot="{ open: teamOpen }" v-model="selectedTeamId">
          <div class="relative">
            <ListboxButton
              class="relative w-36 sm:w-44 cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
              :class="
                teamOpen
                  ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                  : 'rounded-lg'
              "
            >
              <span class="block truncate">
                {{ teams?.find(t => t.simplicate_id === selectedTeamId)?.name || 'Alle teams' }}
              </span>
              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  class="h-5 w-5 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute z-50 w-full rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-sm shadow-lg focus:outline-none max-h-60 overflow-auto"
              >
                <ListboxOption
                  v-if="selectedTeamId"
                  :value="null"
                  v-slot="{ active }"
                  as="template"
                >
                  <li
                    :class="[
                      active
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                        : 'text-gray-900 dark:text-gray-200',
                      'cursor-pointer select-none py-2 px-3'
                    ]"
                  >
                    Alle teams
                  </li>
                </ListboxOption>
                <ListboxOption
                  v-for="team in teams"
                  :key="team.simplicate_id"
                  :value="team.simplicate_id"
                  v-slot="{ active, selected }"
                  as="template"
                >
                  <li
                    :class="[
                      active
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                        : 'text-gray-900 dark:text-gray-200',
                      selected ? 'font-medium' : 'font-normal',
                      'cursor-pointer select-none py-2 px-3'
                    ]"
                  >
                    {{ team.name }}
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>

        <input
          v-model="searchQuery"
          type="text"
          placeholder="Zoeken..."
          class="flex-1 sm:flex-none sm:w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div class="relative">
          <span
            v-if="lastSyncedLabel"
            :title="lastSyncedFull"
            class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
          >
            {{ lastSyncedLabel }}
          </span>
          <button
            @click="startSync"
            :disabled="isSyncing"
            class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap text-sm"
          >
            <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isSyncing }" />
            <span class="hidden sm:inline">Vernieuw</span>
          </button>
        </div>
      </template>
    </PageTabs>

    <!-- Content container with minimum height to prevent layout shifts -->
    <div class="min-h-[400px] mt-6">
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
      >
        <p class="text-red-800 dark:text-red-300">
          Fout bij laden van projecten: {{ error.message }}
        </p>
      </div>

      <!-- Content grid -->
      <AanvragenProspectList v-else :prospects="filteredProspects" @open-detail="openDetailModal" />

      <!-- Empty state -->
      <div v-if="!isLoading && !error && filteredItems.length === 0" class="text-center py-12">
        <BriefcaseIcon class="mx-auto h-12 w-12 text-gray-400" />
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">Geen items gevonden</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Er zijn momenteel geen {{ activeTabLabel.toLowerCase() }} beschikbaar.
        </p>
      </div>
    </div>

    <!-- Detail Modal with preferences, workflow, confirm dialogs -->
    <AanvragenDetailModal
      :selected-prospect="selectedProspect"
      :detail-workflow-status="detailWorkflowStatus"
      :detail-teams="detailTeams"
      :selected-responsible-team-id="selectedResponsibleTeamId"
      :detail-can-edit-team="detailCanEditTeam"
      :team-error="teamError"
      :detail-initial-load-complete="detailInitialLoadComplete"
      :detail-can-edit="detailCanEdit"
      :is-loading-detail="isLoadingDetail"
      :detail-preferences="detailPreferences"
      :detail-employees="detailEmployees"
      :detail-weekly-availability="detailWeeklyAvailability"
      :detail-invalid-dpw-indexes="detailInvalidDpwIndexes"
      :unique-functions="uniqueFunctions"
      :calculate-match-percentage="calculateMatchPercentage"
      :is-perfect-match="isPerfectMatch"
      :get-filtered-employees="getFilteredEmployees"
      :get-selected-employee-names="getSelectedEmployeeNames"
      :detail-save-message="detailSaveMessage"
      :is-detail-error-message="isDetailErrorMessage"
      :detail-can-submit="detailCanSubmit"
      :detail-can-resubmit="detailCanResubmit"
      :detail-is-saving="detailIsSaving"
      :detail-is-submitting="detailIsSubmitting"
      :is-marking-urgent="isMarkingUrgent"
      :can-mark-urgent="canMarkUrgent"
      :is-already-submitted="isAlreadySubmitted"
      :show-confirm-close="showConfirmClose"
      :show-spoed-confirm="showSpoedConfirm"
      @close="handleCloseModal"
      @prospect-updated="handleProspectUpdated"
      @update-team="handleUpdateTeam"
      @add-preference="addDetailPreference"
      @remove-preference="removeDetailPreference"
      @dpw-changed="detailInvalidDpwIndexes.delete($event)"
      @submit="submitDetailForReview"
      @resubmit="resubmitDetailForReview"
      @spoed-click="handleSpoedClick"
      @save-with-tracking="saveDetailPreferencesWithTracking"
      @discard-and-close="discardAndClose"
      @cancel-close="showConfirmClose = false"
      @mark-urgent="handleMarkUrgent"
      @cancel-spoed="showSpoedConfirm = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowPathIcon, BriefcaseIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { prospectsLeadsService } from '@/services/prospectService.js'
import { workflowService } from '@/services/workflowService.js'
import { teamService } from '@/services/employeeService.js'
import { useSyncWithProgress } from '@/composables/data/useSyncWithProgress'
import { useAuth } from '@/composables/auth/useAuth'
import { useProjectDetail } from '@/composables/domain/useProjectDetail'
import { useEmployeeMatching } from '@/composables/domain/useEmployeeMatching'
import { useProspectCache } from '@/composables/data/useProspectCache'
import { invalidateNavBadges } from '@/composables/ui/useNavBadges'
import { useLastSynced } from '@/composables/data/useLastSynced'
import SyncResultNotification from '@/components/common/feedback/SyncResultNotification.vue'
import SyncProgressModal from '@/components/common/modals/SyncProgressModal.vue'
import PageTabs from '@/components/ui/PageTabs.vue'
import AanvragenProspectList from '@/components/aanvragen/AanvragenProspectList.vue'
import AanvragenDetailModal from '@/components/aanvragen/AanvragenDetailModal.vue'

const queryClient = useQueryClient()
const { user } = useAuth()
const { updateProspectDates, updateProspectChanceToScore } = useProspectCache()

const activeTab = ref('mine')
const searchQuery = ref('')
const selectedTeamId = ref(null)

// Fetch teams for filter dropdown
const { data: teams } = useQuery({
  queryKey: ['teams'],
  queryFn: teamService.getAll,
  staleTime: 1000 * 60 * 10
})

// Sync with progress tracking
const {
  isModalOpen,
  isSyncing,
  progress,
  hasError,
  errorMessage,
  syncResult,
  startSync,
  retrySync,
  closeModal
} = useSyncWithProgress('prospects', prospectsLeadsService.sync, {
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
  }
})

const { lastSyncedLabel, lastSyncedFull } = useLastSynced('prospects')

// Fetch prospects/leads
const {
  data: prospects,
  isLoading: isLoadingProspects,
  error: prospectsError,
  refetch: refetchProspects,
  isRefetching: isRefetchingProspects
} = useQuery({
  queryKey: ['prospects-leads'],
  queryFn: prospectsLeadsService.getAll,
  staleTime: 1000 * 60 * 5,
  refetchOnMount: true
})

// Modal state
const selectedProspect = ref(null)
const selectedProspectId = computed(() => selectedProspect.value?.id?.toString() || null)
const showConfirmClose = ref(false)
const originalPreferencesJson = ref('')

// Spoed (urgent) modal state
const showSpoedConfirm = ref(false)
const isMarkingUrgent = ref(false)
const teamError = ref(false)

// Responsible team selection
const selectedResponsibleTeamId = ref(null)

// Use project detail composable for modal
const {
  preferences: detailPreferences,
  teams: detailTeams,
  employees: detailEmployees,
  weeklyAvailability: detailWeeklyAvailability,
  workflowStatus: detailWorkflowStatus,
  isSaving: detailIsSaving,
  isSubmitting: detailIsSubmitting,
  saveMessage: detailSaveMessage,
  invalidDpwIndexes: detailInvalidDpwIndexes,
  isLoadingWorkflow: isLoadingDetail,
  isInitialLoadComplete: detailInitialLoadComplete,
  canEdit: detailCanEdit,
  canEditTeam: detailCanEditTeam,
  canSubmit: detailCanSubmit,
  canResubmit: detailCanResubmit,
  loadTeams: loadDetailTeams,
  addPreference: addDetailPreference,
  removePreference: removeDetailPreference,
  validatePreferences: detailValidatePreferences,
  savePreferences: saveDetailPreferences,
  submitForReview: baseSubmitForReview,
  resubmitForReview: baseResubmitForReview
} = useProjectDetail(selectedProspectId, user)

// Use employee matching composable
const {
  calculateMatchPercentage,
  isPerfectMatch,
  getFilteredEmployees,
  getSelectedEmployeeNames,
  uniqueFunctions
} = useEmployeeMatching(detailWeeklyAvailability, detailEmployees)

const isLoading = computed(() => isLoadingProspects.value)
const error = computed(() => prospectsError.value)
const _isRefetching = computed(() => isRefetchingProspects.value)

const _refetchAll = () => {
  refetchProspects()
}

// Helper function to check if prospect belongs to current user
const isMyProspect = prospect => {
  if (!user.value?.name || !prospect.responsible_employee_name) return false
  return prospect.responsible_employee_name.toLowerCase() === user.value.name.toLowerCase()
}

// Watch for data load to set default tab
watch(
  prospects,
  newProspects => {
    if (newProspects && activeTab.value === 'mine') {
      const myProspectsCount = newProspects.filter(isMyProspect).length
      if (myProspectsCount === 0) {
        activeTab.value = 'all'
      }
    }
  },
  { immediate: true }
)

// Helper: apply team filter to a list
const applyTeamFilter = items => {
  if (!selectedTeamId.value) return items
  return items.filter(p => (p.teams || []).some(t => t.id === selectedTeamId.value))
}

// Computed properties for filtering
const tabs = computed(() => {
  const all = prospects.value || []
  const teamFiltered = applyTeamFilter(all)
  return [
    {
      key: 'mine',
      label: 'Mijn Saleskansen',
      count: applyTeamFilter(all.filter(isMyProspect)).length
    },
    { key: 'all', label: 'Alles', count: teamFiltered.length },
    {
      key: 'verkenning',
      label: 'Verkenning',
      count: teamFiltered.filter(p => p.progress_label === 'Verkenningsfase').length
    },
    {
      key: 'leads',
      label: 'Leads',
      count: teamFiltered.filter(p => p.progress_label === 'Lead fase').length
    },
    {
      key: 'prospects',
      label: 'Prospects',
      count: teamFiltered.filter(p => p.progress_label === 'Prospect fase').length
    }
  ]
})

const activeTabLabel = computed(() => {
  return tabs.value.find(tab => tab.key === activeTab.value)?.label || 'Items'
})

// Sort order for progress labels: Prospect -> Lead -> Verkenning
const progressSortOrder = {
  'Prospect fase': 1,
  'Lead fase': 2,
  Verkenningsfase: 3
}

const sortByProgressAndName = items => {
  return [...items].sort((a, b) => {
    const orderA = progressSortOrder[a.progress_label] || 99
    const orderB = progressSortOrder[b.progress_label] || 99
    if (orderA !== orderB) return orderA - orderB
    return (a.subject || '').localeCompare(b.subject || '', 'nl')
  })
}

const filteredProspects = computed(() => {
  if (!prospects.value) return []

  let filtered = []

  switch (activeTab.value) {
    case 'all':
      filtered = sortByProgressAndName(prospects.value)
      break
    case 'mine':
      filtered = sortByProgressAndName(prospects.value.filter(isMyProspect))
      break
    case 'prospects':
      filtered = prospects.value
        .filter(p => p.progress_label === 'Prospect fase')
        .sort((a, b) => (a.subject || '').localeCompare(b.subject || '', 'nl'))
      break
    case 'leads':
      filtered = prospects.value
        .filter(p => p.progress_label === 'Lead fase')
        .sort((a, b) => (a.subject || '').localeCompare(b.subject || '', 'nl'))
      break
    case 'verkenning':
      filtered = prospects.value
        .filter(p => p.progress_label === 'Verkenningsfase')
        .sort((a, b) => (a.subject || '').localeCompare(b.subject || '', 'nl'))
      break
    default:
      filtered = []
  }

  // Exclude prospects no longer open in Simplicate (preserved for Concretiseren only)
  filtered = filtered.filter(p => p.status !== 'OpportunityStatus_closed')

  // Apply team filter
  if (selectedTeamId.value) {
    filtered = filtered.filter(p => (p.teams || []).some(t => t.id === selectedTeamId.value))
  }

  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      p =>
        p.subject?.toLowerCase().includes(query) ||
        p.organization_name?.toLowerCase().includes(query) ||
        p.responsible_employee_name?.toLowerCase().includes(query)
    )
  }

  return filtered
})

const filteredItems = computed(() => {
  return filteredProspects.value
})

// Check if message is an error
const isDetailErrorMessage = computed(() => {
  return (
    detailSaveMessage.value.includes('Fout') ||
    detailSaveMessage.value.includes('minimaal') ||
    detailSaveMessage.value.includes('geldig')
  )
})

// Check if already submitted (for showing update button)
const isAlreadySubmitted = computed(() => {
  const status = detailWorkflowStatus.value?.status
  return status && status !== 'nieuw'
})

// Check if can mark as urgent (spoed)
// Can only mark when status is 'nieuw' or 'ingediend', not already urgent, and has at least one role
const canMarkUrgent = computed(() => {
  const status = detailWorkflowStatus.value?.status
  const isUrgent = detailWorkflowStatus.value?.is_urgent
  const hasRoles = detailPreferences.value.length > 0
  return ['nieuw', 'ingediend'].includes(status) && !isUrgent && hasRoles
})

// Check for unsaved changes
const hasUnsavedChanges = computed(() => {
  if (!selectedProspect.value) return false
  // Don't report changes until initial load is complete
  if (!detailInitialLoadComplete.value) return false
  const currentJson = JSON.stringify(detailPreferences.value)
  return currentJson !== originalPreferencesJson.value
})

// Wrapper for savePreferences that updates change tracking after success
const saveDetailPreferencesWithTracking = async () => {
  const result = await saveDetailPreferences()
  if (result) {
    // Update originalPreferencesJson so hasUnsavedChanges becomes false
    originalPreferencesJson.value = JSON.stringify(detailPreferences.value)
    // Bevestigingsbericht tonen
    detailSaveMessage.value = 'Voorkeuren zijn bijgewerkt'
    setTimeout(() => {
      detailSaveMessage.value = ''
    }, 3000)
  }
  return result
}

// Validate team selection before workflow actions
const validateTeamSelection = () => {
  if (!selectedResponsibleTeamId.value) {
    teamError.value = true
    detailSaveMessage.value = 'Selecteer een verantwoordelijk team'
    return false
  }
  return true
}

// Handle team update from detail modal
const handleUpdateTeam = teamId => {
  selectedResponsibleTeamId.value = teamId
  teamError.value = false
}

// Wrapper for submitForReview that updates change tracking after success and closes modal
const submitDetailForReview = async () => {
  if (!validateTeamSelection()) return false
  const result = await baseSubmitForReview({
    responsible_team_id: selectedResponsibleTeamId.value
  })
  if (result) {
    // Update originalPreferencesJson so hasUnsavedChanges becomes false
    originalPreferencesJson.value = JSON.stringify(detailPreferences.value)
    // Close modal after successful submission
    closeDetailModal()
  }
  return result
}

// Wrapper for resubmitForReview that updates change tracking after success and closes modal
const resubmitDetailForReview = async () => {
  if (!validateTeamSelection()) return false
  const result = await baseResubmitForReview({
    responsible_team_id: selectedResponsibleTeamId.value
  })
  if (result) {
    // Update originalPreferencesJson so hasUnsavedChanges becomes false
    originalPreferencesJson.value = JSON.stringify(detailPreferences.value)
    // Close modal after successful resubmission
    closeDetailModal()
  }
  return result
}

// Watch for initial load completion to store original state
watch(detailInitialLoadComplete, isComplete => {
  if (isComplete && selectedProspect.value) {
    originalPreferencesJson.value = JSON.stringify(detailPreferences.value)
  }
})

// Modal functions
const openDetailModal = async prospect => {
  selectedProspect.value = prospect
  // Initialize responsible team from prospect data (comes from psw.responsible_team_id in list)
  selectedResponsibleTeamId.value = prospect.responsible_team_id || null
  // Load teams when modal opens
  await loadDetailTeams()
}

// Update selectedResponsibleTeamId when workflow status loads (more accurate than list data)
watch(detailWorkflowStatus, ws => {
  if (ws?.responsible_team_id) {
    selectedResponsibleTeamId.value = ws.responsible_team_id
  }
})

// Handle prospect updates from the detail modal
const handleProspectUpdated = updatedProspect => {
  updateProspectDates(updatedProspect)
  updateProspectChanceToScore(updatedProspect)
  // Update local selectedProspect if still open
  if (selectedProspect.value?.id === updatedProspect.id) {
    selectedProspect.value = { ...selectedProspect.value, ...updatedProspect }
  }
}

const closeDetailModal = () => {
  selectedProspect.value = null
  showConfirmClose.value = false
  originalPreferencesJson.value = ''
  selectedResponsibleTeamId.value = null
  // Refresh prospects list to get updated workflow status
  queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
}

// Handle close with unsaved changes check
const handleCloseModal = () => {
  if (hasUnsavedChanges.value) {
    showConfirmClose.value = true
  } else {
    closeDetailModal()
  }
}

// Discard changes and close
const discardAndClose = () => {
  closeDetailModal()
}

// Handle spoed button click: validate before showing confirm modal
const handleSpoedClick = () => {
  if (!detailValidatePreferences()) return
  if (!validateTeamSelection()) return
  showSpoedConfirm.value = true
}

// Handle marking prospect as urgent (spoed)
const handleMarkUrgent = async () => {
  if (!selectedProspect.value || !user.value) return

  isMarkingUrgent.value = true
  try {
    // First save the preferences (same as regular submit flow)
    const saved = await saveDetailPreferences()
    if (!saved) {
      // saveDetailPreferences already shows error message
      return
    }

    await workflowService.markUrgent(
      selectedProspect.value.id,
      {
        name: user.value.name,
        email: user.value.email
      },
      {
        responsible_team_id: selectedResponsibleTeamId.value
      }
    )

    // Invalidate beoordelen badge - new urgent submission waiting for review
    invalidateNavBadges(queryClient, { beoordelen: true })

    // Close both modals after successful submission
    showSpoedConfirm.value = false
    closeDetailModal()
  } catch (error) {
    console.error('Error marking as urgent:', error)
    detailSaveMessage.value = error.response?.data?.error || 'Fout bij markeren als spoed'
  } finally {
    isMarkingUrgent.value = false
  }
}
</script>
